import {
  SeaAllFilterField,
  TagAction,
} from '@aibees/elephant/components/see-all-popup';
import { CompanyDescription } from '@aibees/elephant/feature/search-drawer/components/company-description';
import { CompanySeoDescription } from '@aibees/elephant/feature/search-drawer/components/company-seo-description copy';
import { ProductAndServicesInfo } from '@aibees/elephant/feature/search-drawer/feature/overview/components/product-and-services-info';
import { companyTypeFilterLabelParser } from '@aibees/elephant/feature/search-filters/components/filter-tags/utils';
import { getCompanySizeRange } from '@aibees/elephant/feature/search-table-company/utils';
import {
  useSearchTableFromRoute,
  useReadCompany,
} from '@aibees/elephant/hooks';
import { SearchTable } from '@aibees/elephant/types';
import { formatCompanyLocation } from '@aibees/elephant/utils';
import {
  Icon,
  IconButton,
  LinkButton,
  Scrollbar,
  TextEllipsis,
} from '@aibees/shared/components';
import { capitalize, classnames } from '@aibees/shared/utils';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { getCompanyDomain } from '../company-details/utils';
import { getCompanyInfoSectionVisibility } from '../company-info/utils';
import { SearchSocialMedia } from '../search-social-media';
import { SocialLink } from '../search-social-media-item/types';
import * as Styled from './styles';
import * as SX from './sx';
import type { CompanyProfileDetailPopoverProps as Props } from './types';

const POPOVER_ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'center' as const,
};
const POPOVER_TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'center' as const,
};
const POPOVER_SLOT_PROPS = { paper: { sx: SX.popover } };

const AvatarFallbackIcon = memo(() => (
  <Icon size={20} name="business" color="grey.400" />
));
AvatarFallbackIcon.displayName =
  'CompanyProfileDetailPopover.AvatarFallbackIcon';

const WorldIcon = memo(() => <Icon name="world" color="grey.500" size={18} />);
const GroupsIcon = memo(() => (
  <Icon name="groups-outlined" color="grey.500" size={18} />
));
const LocationIcon = memo(() => (
  <Icon name="location" color="grey.500" size={18} />
));
const EngineeringIcon = memo(() => (
  <Icon name="engineering-outline" color="grey.500" size={18} />
));
const TypeIcon = memo(() => <Icon name="ic-type" color="grey.500" size={18} />);

const PrimaryDivider = memo(() => {
  const theme = useTheme();
  return <Divider color={theme.palette.primary[100]} />;
});
PrimaryDivider.displayName = 'CompanyProfileDetailPopover.PrimaryDivider';

export const CompanyProfileDetailPopover = memo(
  ({ className, companyId, disabled = false }: Props) => {
    const table = useSearchTableFromRoute();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const { data, isLoading } = useReadCompany(
      anchorEl ? companyId : undefined
    );
    const companySize = getCompanySizeRange(data?.summary?.staff);
    const domain = getCompanyDomain(data?.link);

    const { showOverview, showSeo, showProducts } =
      getCompanyInfoSectionVisibility(data);

    const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const actions = useMemo(
      () =>
        table === SearchTable.People || table === SearchTable.Company
          ? [
              TagAction.INCLUDE_IN_EXISTING_SEARCH,
              TagAction.INCLUDE_IN_NEW_SEARCH,
              TagAction.EXCLUDE_IN_EXISTING_SEARCH,
              TagAction.EXCLUDE_IN_NEW_SEARCH,
            ]
          : [TagAction.INCLUDE_IN_NEW_SEARCH, TagAction.EXCLUDE_IN_NEW_SEARCH],
      [table]
    );

    return (
      <Styled.Wrapper
        className={classnames('CompanyProfileDetailPopover', className)}
        sx={SX.root}
      >
        <IconButton
          icon="notes-outline"
          sx={SX.box}
          onClick={handleClick}
          size="xsmall"
          variant="outlined"
          iconSize={12}
          disabled={disabled}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={POPOVER_ANCHOR_ORIGIN}
          transformOrigin={POPOVER_TRANSFORM_ORIGIN}
          slotProps={POPOVER_SLOT_PROPS}
        >
          {open && (
            <>
              {isLoading ? (
                <Box sx={SX.loading}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Stack direction="row" gap={0.5} alignItems="center" p={1.5}>
                    <Avatar
                      src={data?.summary?.logo?.source || undefined}
                      alt={data?.summary?.name ?? undefined}
                      size={48}
                      color="white"
                      variant="rounded"
                    >
                      <AvatarFallbackIcon />
                    </Avatar>
                    <Stack justifyContent="space-between">
                      <TextEllipsis
                        variant="h6"
                        color="grey.700"
                        fontWeight={700}
                        w="295px"
                        value={data?.summary?.name ?? undefined}
                      />

                      <SearchSocialMedia
                        socialLink={data?.link as SocialLink}
                        hideLinkOutIcon={false}
                      />
                    </Stack>
                  </Stack>
                  <PrimaryDivider />

                  <Scrollbar sx={SX.scrollbar}>
                    <Stack gap={1}>
                      <Stack gap={1}>
                        {showOverview && (
                          <CompanyDescription
                            isDetailPopover
                            data={data}
                            sx={SX.companyDescription}
                          />
                        )}
                        {showSeo && (
                          <CompanySeoDescription
                            isDetailPopover
                            data={data}
                            sx={SX.companyDescription}
                          />
                        )}
                      </Stack>

                      <Stack gap={1}>
                        {domain && (
                          <Stack gap={1}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <WorldIcon />
                              <Typography
                                variant="body3"
                                fontWeight={700}
                                color="grey.700"
                              >
                                Domain:
                              </Typography>
                            </Stack>
                            <LinkButton
                              variant="text"
                              href={domain}
                              sx={SX.domainLink}
                            >
                              <Typography
                                variant="body4"
                                fontWeight={400}
                                color="grey.700"
                              >
                                {domain}
                              </Typography>
                            </LinkButton>
                          </Stack>
                        )}
                        {!!companySize && (
                          <Stack gap={1}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <GroupsIcon />
                              <Typography
                                variant="body3"
                                fontWeight={700}
                                color="grey.700"
                              >
                                Employee Size:
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body4"
                              fontWeight={400}
                              color="grey.700"
                            >
                              {companySize}
                            </Typography>
                          </Stack>
                        )}
                        {(data?.location?.headquarter?.country ||
                          data?.location?.headquarter?.city) && (
                          <Stack gap={1}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <LocationIcon />
                              <Typography
                                variant="body3"
                                fontWeight={700}
                                color="grey.700"
                              >
                                Company Location:
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body4"
                              fontWeight={400}
                              color="grey.700"
                            >
                              {formatCompanyLocation([
                                data?.location?.headquarter?.city,
                                data?.location?.headquarter?.country,
                              ])}
                            </Typography>
                          </Stack>
                        )}

                        {data?.summary?.industry && (
                          <Stack gap={1}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <EngineeringIcon />
                              <Typography
                                variant="body3"
                                fontWeight={700}
                                color="grey.700"
                              >
                                Primary Industry:
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body4"
                              fontWeight={400}
                              color="grey.700"
                            >
                              {capitalize(data.summary.industry)}
                            </Typography>
                          </Stack>
                        )}

                        {data?.summary?.type && (
                          <Stack gap={1}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <TypeIcon />
                              <Typography
                                variant="body3"
                                fontWeight={700}
                                color="grey.700"
                              >
                                Type:
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body4"
                              fontWeight={400}
                              color="grey.700"
                            >
                              {companyTypeFilterLabelParser(
                                data?.summary.type,
                                data?.summary.type
                              )}
                            </Typography>
                          </Stack>
                        )}

                        {showProducts && (
                          <ProductAndServicesInfo
                            config={{
                              actions,
                              field: SeaAllFilterField.industry,
                              placeholder: 'Search Product & Services',
                            }}
                            isDetailPopover
                            data={data}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Scrollbar>
                </>
              )}
            </>
          )}
        </Popover>
      </Styled.Wrapper>
    );
  }
);

CompanyProfileDetailPopover.displayName = 'CompanyProfileDetailPopover';
